/**
 * `wayai knowledge migrate`: an org's knowledge resources, then each hub's, moved into the org's
 * knowledge base as file types and files (#5142 step 4, PR 3a).
 *
 * **Where it writes.** A file type is config, written in a preview base and promoted; a file is
 * data, written where agents will read it. So the file types are written in the knowledge base's
 * preview and promoted, and the files are written in the production base (#5083 decision 112, O3).
 * The promotion names every other config section empty, so no other config moves with it; like
 * every promotion, it also publishes the credentials the preview opted in to production. The
 * preview gets none of the files.
 *
 * **Org first.** Without `--hub` it moves the org's resources. With `--hub` it moves that hub's
 * own resources, read from its production hub, and refuses until the org's move has finished:
 * every org file type in production as this run would write it, and every org file at its path.
 * That is read from the base itself, so nothing records a run.
 *
 * **Nothing half-decided.** Everything is read and compared first. A reason to stop (a file type id
 * another resource holds, a folder description whose `_index.md` a file already holds, a production
 * file that differs from its source) stops the run before its first write. A production file is
 * never overwritten: one that differs from its source is an edit this run did not make.
 *
 * **Idempotent.** A file type production already holds is never written again (#5083 decision
 * 126) — an edit made to it after the move included — and a new file its schema or limits refuse
 * stops the run before any write, naming the field and the value; a file already at its path with
 * the same content, content type and metadata is not written again. A re-run of a finished
 * migration writes nothing.
 */

import { isDeepStrictEqual } from 'node:util';
import { computeHash } from '../lib/resource-files.js';
import { expected } from '../lib/expected.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { slugify } from '../lib/utils.js';
import {
  describeSkip,
  describeStop,
  emptyDesired,
  hubFileTypeId,
  limitRefusal,
  metadataRefusals,
  orgFileTypeId,
  planDesired,
  provenanceOf,
  toolBase,
  type ClaimedId,
  type DesiredFile,
  type DesiredState,
  type FileTypeSpec,
  type HeldFileType,
  type ResourceTarget,
  type SchemaNote,
} from './plan.js';
import {
  hubSources,
  movesFromHub,
  movesFromOrg,
  orgSources,
  type ContentRef,
  type HubFileRow,
  type HubFolderRow,
  type HubResourceRow,
  type OrgFileRow,
  type OrgFolderRow,
  type OrgResourceRow,
  type ResourceRows,
  type SourceResource,
} from './source.js';

/** One hub the caller can see, production and previews alike. */
export interface HubListing {
  hub_id: string;
  hub_name: string;
  hub_environment: string;
  production_hub_id: string | null;
}

/** WayAI's side: the knowledge resources the migration reads. */
export interface KnowledgeReader {
  listOrgResources(): Promise<OrgResourceRow[]>;
  listOrgResourceFiles(orgResourceId: string): Promise<OrgFileRow[]>;
  listOrgResourceFolders(orgResourceId: string): Promise<OrgFolderRow[]>;
  listHubs(): Promise<HubListing[]>;
  listHubResources(hubId: string): Promise<HubResourceRow[]>;
  listHubResourceFiles(hubId: string, resourceId: string): Promise<HubFileRow[]>;
  listHubResourceFolders(hubId: string, resourceId: string): Promise<HubFolderRow[]>;
  readContent(ref: ContentRef): Promise<Uint8Array>;
}

export interface BaseRow {
  id: string;
  environment?: string;
  origin_base_id?: string | null;
  deleted?: boolean;
}

export interface RemoteFileType {
  id: string;
  name: string;
  description?: string;
  max_size?: number;
  allowed_content_types?: string[];
  metadata_schema?: unknown;
}

export interface RemoteFile {
  path: string;
  content_sha256: string;
  content_type: string;
  metadata: Record<string, unknown>;
}

/** The data side: the knowledge base and its preview. */
export interface KnowledgeBase {
  getBase(id: string): Promise<BaseRow | null>;
  listPreviews(id: string): Promise<BaseRow[]>;
  listFileTypes(base: string): Promise<RemoteFileType[]>;
  getFileType(base: string, id: string): Promise<RemoteFileType | null>;
  putFileType(base: string, spec: FileTypeSpec): Promise<void>;
  /** The data product's promotion of the named file types: its answer, with nothing applied on a dry run. */
  promoteFileTypes(production: string, preview: string, ids: string[], dryRun: boolean): Promise<unknown>;
  listFiles(base: string, fileType: string): Promise<RemoteFile[]>;
  putFile(base: string, file: DesiredFile, bytes: Uint8Array): Promise<void>;
}

export interface MigrateOptions {
  orgId: string;
  base: string;
  hubId?: string;
  dryRun: boolean;
  yes: boolean;
}

export interface MigrateIo {
  log: (line: string) => void;
  /** Asked before the first write; false cancels. */
  confirm: (question: string) => Promise<boolean>;
}

/** What a run wrote, or would have written on a dry run. */
export interface MigrateResult {
  production: string;
  preview: string;
  fileTypesWritten: string[];
  fileTypesPromoted: string[];
  filesWritten: string[];
  notes: SchemaNote[];
  written: boolean;
}

/** A production file this run would replace: an edit it did not make. */
interface Conflict {
  fileType: string;
  path: string;
  what: string;
}

const safe = (text: string) => sanitizeTerminalText(text);

/** The knowledge base's production base and the preview its file types are staged in. */
async function resolveBases(kb: KnowledgeBase, baseId: string): Promise<{ production: string; preview: string }> {
  const base = await kb.getBase(baseId);
  if (!base || base.deleted) throw expected(`Base "${safe(baseId)}" not found in this organization.`);
  if (base.environment === 'preview') {
    const origin = base.origin_base_id ? await kb.getBase(base.origin_base_id) : null;
    if (!origin || origin.deleted || origin.environment !== 'production') {
      throw expected(
        `Base "${safe(baseId)}" is a preview with no production origin, so its file types can never be promoted. ` +
          'Name the production knowledge base, or a preview created from it.',
      );
    }
    return { production: origin.id, preview: base.id };
  }
  // The listing can repeat a preview, or still show a deleted one, until its store settles: each
  // candidate is read back from the base itself.
  const candidates = [...new Set((await kb.listPreviews(base.id)).filter((p) => p.origin_base_id === base.id).map((p) => p.id))];
  const previews: string[] = [];
  for (const id of candidates) {
    const preview = await kb.getBase(id);
    if (preview && !preview.deleted && preview.environment === 'preview' && preview.origin_base_id === base.id) previews.push(id);
  }
  if (previews.length !== 1) {
    throw expected(
      previews.length === 0
        ? `Base "${safe(baseId)}" has no preview to write its file types in. Create one with \`wayai bases create-preview ${safe(baseId)} --name <name>\`.`
        : `Base "${safe(baseId)}" has ${previews.length} previews (${previews.map(safe).join(', ')}). Pass the one to write its file types in as --base.`,
    );
  }
  return { production: base.id, preview: previews[0] };
}

async function readOrg(reader: KnowledgeReader): Promise<SourceResource[]> {
  const resources = await reader.listOrgResources();
  const rows = new Map<string, ResourceRows<OrgFileRow, OrgFolderRow>>();
  for (const resource of resources.filter(movesFromOrg)) {
    rows.set(resource.org_resource_id, {
      files: await reader.listOrgResourceFiles(resource.org_resource_id),
      folders: await reader.listOrgResourceFolders(resource.org_resource_id),
    });
  }
  return orgSources(resources, rows);
}

/** A hub's own resources; with `withRows: false`, only what their file type ids need (no files). */
async function readHub(reader: KnowledgeReader, hubId: string, withRows = true): Promise<SourceResource[]> {
  const resources = await reader.listHubResources(hubId);
  const rows = new Map<string, ResourceRows<HubFileRow, HubFolderRow>>();
  if (withRows) {
    for (const resource of resources.filter(movesFromHub)) {
      rows.set(resource.resource_id, {
        files: await reader.listHubResourceFiles(hubId, resource.resource_id),
        folders: await reader.listHubResourceFolders(hubId, resource.resource_id),
      });
    }
  }
  return hubSources(hubId, resources, rows);
}

function orgTargets(sources: SourceResource[]): ResourceTarget[] {
  return sources.map((resource) => ({ resource, derivedId: orgFileTypeId(resource), displayName: resource.name }));
}

function hubTargets(hub: HubListing, hubSlug: string, sources: SourceResource[]): ResourceTarget[] {
  return sources.map((resource) => ({
    resource,
    derivedId: hubFileTypeId(hubSlug, resource),
    displayName: `${resource.name} (${hub.hub_name})`,
  }));
}

/** The production hub `hubId` names: itself, or the one a preview hub publishes to. */
function productionHub(hubs: HubListing[], hubId: string): HubListing {
  const hub = hubs.find((h) => h.hub_id === hubId);
  if (!hub) throw expected(`Hub ${safe(hubId)} not found in this organization, or you cannot read it.`);
  if (hub.hub_environment === 'production') return hub;
  const production = hub.production_hub_id ? hubs.find((h) => h.hub_id === hub.production_hub_id) : undefined;
  if (!production) {
    throw expected(
      `Hub ${safe(hubId)} is a preview that has not been published, and a hub's knowledge moves from its production hub. Publish it first.`,
    );
  }
  return production;
}

function sameFileType(remote: RemoteFileType | null | undefined, spec: FileTypeSpec): boolean {
  return (
    !!remote &&
    remote.name === spec.name &&
    (remote.description ?? '') === spec.description &&
    (remote.max_size ?? 0) === spec.max_size &&
    isDeepStrictEqual(remote.allowed_content_types ?? [], spec.allowed_content_types) &&
    isDeepStrictEqual(remote.metadata_schema ?? null, spec.metadata_schema)
  );
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * What a promotion's answer says it would do beyond promoting `ids`: an entry of any other
 * section, a removal, or a check it reports as incompatible.
 */
function promotionProblems(result: unknown, ids: Set<string>): string[] {
  const answer = isObject(result) ? result : {};
  const problems: string[] = [];
  for (const [section, bucket] of Object.entries(isObject(answer.diff) ? answer.diff : {})) {
    if (!isObject(bucket)) continue;
    for (const change of ['added', 'modified', 'removed'] as const) {
      for (const entry of Array.isArray(bucket[change]) ? bucket[change] : []) {
        const row = isObject(entry) ? (isObject(entry.after) ? entry.after : entry) : {};
        const id = typeof row.id === 'string' ? row.id : JSON.stringify(row).slice(0, 80);
        if (section === 'file_types' && change !== 'removed' && ids.has(id)) continue;
        problems.push(`it would also apply ${section} ${change}: "${id}"`);
      }
    }
  }
  for (const [check, entries] of Object.entries(answer)) {
    if (!check.endsWith('compatibility') || !Array.isArray(entries)) continue;
    for (const entry of entries) {
      if (isObject(entry) && entry.compatible === false) problems.push(`${check} fails: ${JSON.stringify(entry).slice(0, 200)}`);
    }
  }
  return problems;
}

function bodyBytes(reader: KnowledgeReader, file: DesiredFile): Promise<Uint8Array> {
  return file.body.kind === 'text' ? Promise.resolve(new TextEncoder().encode(file.body.text)) : reader.readContent(file.body.ref);
}

/** Production's side of the plan: its file types, and the files of those the planned resources may take. */
class Production {
  readonly files = new Map<string, Map<string, RemoteFile>>();

  private constructor(
    private readonly kb: KnowledgeBase,
    readonly base: string,
    readonly types: Map<string, RemoteFileType>,
  ) {}

  static async read(kb: KnowledgeBase, base: string): Promise<Production> {
    return new Production(kb, base, new Map((await kb.listFileTypes(base)).map((ft) => [ft.id, ft])));
  }

  /** The file type a resource already has here, found by the resource its schema names, or undefined. */
  idFor(sourceKey: string): string | undefined {
    for (const [id, ft] of this.types) if (provenanceOf(ft.metadata_schema) === sourceKey) return id;
    return undefined;
  }

  /** What the plan reads: every file type with the resource it names. Lists the files of those `targets` may take, for the compare. */
  async held(targets: ResourceTarget[]): Promise<Map<string, HeldFileType>> {
    const keys = new Set(targets.map((t) => t.resource.sourceKey));
    const wanted = new Set(targets.map((t) => t.derivedId));
    const held = new Map<string, HeldFileType>();
    for (const [id, ft] of this.types) {
      const source = provenanceOf(ft.metadata_schema);
      if (source && keys.has(source)) wanted.add(id);
      held.set(id, { source, fileType: ft });
    }
    for (const id of wanted) {
      if (!held.has(id) || this.files.has(id)) continue;
      this.files.set(id, new Map((await this.kb.listFiles(this.base, id)).map((f) => [f.path, f])));
    }
    return held;
  }
}

/**
 * Whether an org's move has finished on the production base: every org file type there as
 * `desired` plans it — and the plan keeps a file type production holds as it is, never writing it
 * again, so an admin's later edit counts as finished — and every org file at its path. Content is not
 * compared: a file is written whole or not at all, so a path that is there was written.
 */
function orgUnfinished(production: Production, desired: DesiredState): string | null {
  if (desired.stops.length > 0) return `it stops on ${desired.stops.length} reason(s)`;
  const pendingTypes = desired.fileTypes.filter((spec) => !sameFileType(production.types.get(spec.id), spec)).length;
  const pendingFiles = desired.files.filter((f) => !production.files.get(f.fileType)?.has(f.path)).length;
  if (pendingTypes === 0 && pendingFiles === 0) return null;
  return `${pendingTypes} file type(s) and ${pendingFiles} file(s) are not in "${production.base}" as it writes them`;
}

function printNotes(io: MigrateIo, notes: SchemaNote[]): void {
  const widened = notes.filter((n) => n.reason !== 'undeclared');
  const declared = notes.filter((n) => n.reason === 'undeclared');
  if (widened.length > 0) {
    io.log('\nMetadata schema widened to keep values it would refuse (K21):');
    for (const note of widened) {
      const why =
        note.reason === 'invalid'
          ? 'its schema does not compile, or refers to something its source does not hold, so it accepts any value'
          : 'it now also accepts';
      io.log(`  ${safe(note.fileType)}.${safe(note.field)}: ${why}`);
      for (const { path, value } of note.values) io.log(`    ${safe(JSON.stringify(value))} in ${safe(path)}`);
    }
  }
  if (declared.length > 0) {
    io.log('\nMetadata fields no schema declared, now declared so a filter on them is answered:');
    for (const note of declared) io.log(`  ${safe(note.fileType)}.${safe(note.field)}`);
  }
}

/** Run one migration: org mode without `hubId`, hub mode with it. */
export async function migrateKnowledge(
  reader: KnowledgeReader,
  kb: KnowledgeBase,
  opts: MigrateOptions,
  io: MigrateIo,
): Promise<MigrateResult> {
  const { production: productionId, preview } = await resolveBases(kb, opts.base);
  // The hub is named before anything heavier is read, so a hub id that cannot move is refused first.
  const hubs = opts.hubId === undefined ? [] : await reader.listHubs();
  const hub = opts.hubId === undefined ? undefined : productionHub(hubs, opts.hubId);
  const production = await Production.read(kb, productionId);
  const orgPlanned = orgTargets(await readOrg(reader));

  let desired: DesiredState;
  let scope: string;
  if (!hub) {
    desired = planDesired(orgPlanned, { production: await production.held(orgPlanned) });
    scope = `organization ${opts.orgId}`;
  } else {
    const orgDesired = planDesired(orgPlanned, { production: await production.held(orgPlanned) });
    const unfinished = orgUnfinished(production, orgDesired);
    if (unfinished) {
      throw expected(
        `Hub mode runs once the organization's knowledge has moved, and ${unfinished}. ` +
          `Run \`wayai knowledge migrate --org ${opts.orgId} --base ${safe(opts.base)}\` first.`,
      );
    }
    // The same rule as the hub's folder slug (`getHubFolderSlug`), taken from its name now.
    const hubSlug = slugify(hub.hub_name);
    scope = `hub "${safe(hub.hub_name)}" (${hub.hub_id})`;
    if (!hubSlug) {
      desired = emptyDesired([{ kind: 'hub_slug_empty', hub: hub.hub_name }]);
    } else {
      const orgIds = orgDesired.fileTypes.map((spec) => spec.id);
      const claimed: ClaimedId[] = orgIds.map((id) => ({ id, owner: `org file type "${id}"` }));
      // A hub whose name derives the same slug derives ids of the same shape in the same base (K8).
      // One that has moved holds the ids production keeps for it, whatever its name derives now.
      for (const other of hubs) {
        if (other.hub_id === hub.hub_id || other.hub_environment !== 'production' || slugify(other.hub_name) !== hubSlug) continue;
        for (const target of hubTargets(other, hubSlug, await readHub(reader, other.hub_id, false))) {
          const id = production.idFor(target.resource.sourceKey) ?? target.derivedId;
          claimed.push({ id, owner: `hub "${other.hub_name}" (${other.hub_id})'s resource "${target.resource.name}"` });
        }
      }
      const planned = hubTargets(hub, hubSlug, await readHub(reader, hub.hub_id));
      desired = planDesired(planned, { production: await production.held(planned), claimed, sharedTools: orgIds });
    }
  }

  // ---- Compare with the base -------------------------------------------------------------
  // A file type production already holds as planned is left alone, on the preview too: the
  // preview is written only to promote from it.
  const toPromote = desired.fileTypes.filter((spec) => !sameFileType(production.types.get(spec.id), spec));
  const toWrite: FileTypeSpec[] = [];
  for (const spec of toPromote) {
    if (!sameFileType(await kb.getFileType(preview, spec.id), spec)) toWrite.push(spec);
  }

  const creates: DesiredFile[] = [];
  const conflicts: Conflict[] = [];
  const limited: string[] = [];
  const specs = new Map(desired.fileTypes.map((spec) => [spec.id, spec]));
  let unchanged = 0;
  for (const file of desired.files) {
    const there = production.files.get(file.fileType)?.get(file.path);
    if (!there) {
      // What the data product will check on the write, checked first: the file type's limits, and
      // the whole metadata against its schema — a moved file type's as production holds it, which a
      // re-run never changes (#5083 decision 126).
      const spec = specs.get(file.fileType);
      if (spec) {
        const kept = production.types.has(file.fileType);
        const limit = limitRefusal(spec, file.contentType, file.size);
        if (limit) {
          const why = `file type "${file.fileType}" ${kept ? 'in production ' : ''}refuses it: ${limit}`;
          limited.push(describeStop({ kind: 'limit_refuses', fileType: file.fileType, path: file.path, why }));
        }
        for (const { field, why } of metadataRefusals(spec.metadata_schema, file.metadata)) {
          const value = field === null ? undefined : file.metadata[field];
          limited.push(describeStop({ kind: 'metadata_refused', fileType: file.fileType, resource: file.resource, path: file.path, field, value, why, kept }));
        }
      }
      creates.push(file);
      continue;
    }
    const differs: string[] = [];
    if (there.content_type !== file.contentType) differs.push('content type');
    if (!isDeepStrictEqual(there.metadata ?? {}, file.metadata)) differs.push('metadata');
    if (differs.length === 0 && there.content_sha256 !== computeHash(await bodyBytes(reader, file))) differs.push('content');
    if (differs.length > 0) conflicts.push({ fileType: file.fileType, path: file.path, what: differs.join(', ') });
    else unchanged++;
  }

  // ---- Report -----------------------------------------------------------------------------
  io.log(`Knowledge of ${scope} → base "${safe(productionId)}" (file types written in "${safe(preview)}")`);
  io.log('\nFile types:');
  if (desired.fileTypes.length === 0) io.log('  (none)');
  for (const spec of desired.fileTypes) {
    const mark = toPromote.includes(spec) ? (production.types.has(spec.id) ? '~' : '+') : '=';
    const alias = toolBase(spec.id) === spec.id ? '' : ` — tool name alias "${safe(spec.name)}"`;
    io.log(`  ${mark} ${safe(spec.id)}${alias}`);
  }
  printNotes(io, desired.notes);
  io.log(`\nFiles in "${safe(productionId)}":`);
  for (const file of creates) io.log(`  + ${safe(file.fileType)}/${safe(file.path)}`);
  io.log(`  ${creates.length} to write, ${unchanged} already there.`);
  for (const skip of desired.skips) io.log(`  ${safe(describeSkip(skip))}`);

  const reasons = [
    ...desired.stops.map((stop) => describeStop(stop)),
    ...limited,
    ...conflicts.map((c) => `${c.fileType}/${c.path}: production's copy differs from its source (${c.what}). It was edited after it moved, so it is not overwritten. Remove it from production to move it again.`),
  ];
  const result: MigrateResult = {
    production: productionId,
    preview,
    fileTypesWritten: toWrite.map((s) => s.id),
    fileTypesPromoted: toPromote.map((s) => s.id),
    filesWritten: creates.map((f) => `${f.fileType}/${f.path}`),
    notes: desired.notes,
    written: false,
  };
  if (reasons.length > 0) {
    io.log('\nStopped:');
    for (const reason of reasons) io.log(`  ${safe(reason)}`);
    throw expected(`The migration stops on ${reasons.length} reason(s) above; nothing was written.`);
  }

  const writes = toWrite.length + toPromote.length + creates.length;
  if (opts.dryRun) {
    io.log(`\nDry run: nothing written (${writes === 0 ? 'nothing to write' : `${writes} write(s) planned`}).`);
    return result;
  }
  if (writes === 0) {
    io.log('\nNothing to write: this knowledge has moved.');
    return result;
  }
  const question =
    `Write ${toWrite.length} file type(s) in "${preview}", promote ${toPromote.length} into "${productionId}", ` +
    `and write ${creates.length} file(s) in "${productionId}"?`;
  if (!opts.yes && !(await io.confirm(question))) {
    io.log('Cancelled.');
    return result;
  }

  // ---- Write ------------------------------------------------------------------------------
  for (const spec of toWrite) await kb.putFileType(preview, spec);
  if (toPromote.length > 0) {
    const ids = toPromote.map((s) => s.id);
    // The data product's own verdict first: a dry run must change only these file types, and
    // report nothing incompatible, before production is touched.
    const problems = promotionProblems(await kb.promoteFileTypes(productionId, preview, ids, true), new Set(ids));
    if (problems.length > 0) {
      throw expected(
        `The promotion into "${safe(productionId)}" would not apply as planned (${problems.map(safe).join('; ')}). ` +
          `Its file types are written in "${safe(preview)}"; nothing reached production.`,
      );
    }
    await kb.promoteFileTypes(productionId, preview, ids, false);
    for (const spec of toPromote) {
      if (!sameFileType(await kb.getFileType(productionId, spec.id), spec)) {
        throw new Error(`The promotion into "${productionId}" did not leave file type "${spec.id}" as planned; no file was written.`);
      }
    }
  }
  for (const file of creates) await kb.putFile(productionId, file, await bodyBytes(reader, file));
  io.log(
    `\nDone: ${toWrite.length} file type(s) written, ${toPromote.length} promoted, ${creates.length} file(s) written. ` +
      `The preview holds none of these files.`,
  );
  return { ...result, written: true };
}
